'use client'

import { useEffect, useRef, useState } from 'react'
import {
  createPlayer,
  type CreatePlayerOptions,
  type LoadRequest,
  type PlayerInterface,
} from '@foundation-player/api'
import type { GeoCountries, VideoProps } from '@/types/videoTypes'
import { FALLBACK_IMAGE_URL } from '../Image/FallbackImage'
import {
  createLoadRequest,
  dateErrorMessage,
  defaultErrorMessage,
  geoBlockingErrorMessage,
} from './config'
import { fetch as apiFetch } from '@/services/api/client'
import { geoAllowed, toGeoCountries } from '@/utils/videoPlayer'
import { useTcString } from '../../privacy/useTcString'

export type FoundationPlayerProps = {
  video: VideoProps
  postRollDisabled?: boolean
  autoplay?: boolean
  currentVideoIndex?: number
  isAdPlayingListener?: (value: boolean) => void
  currentAdTimeListener?: (value: number) => void
  contentFinishedListener?: (value: boolean) => void
  videoPlayingListener?: (value: boolean) => void
  showRelatedVideosListener?: (value: boolean) => void
  customPlayButton?: boolean
  fast?: boolean
}

const DEBUG = process.env.NEXT_PUBLIC_PLAYER_DEBUG === 'true' || true

const log = {
  info: (...args: any[]) =>
    DEBUG && console.info('[FoundationPlayer]', ...args),
  warn: (...args: any[]) =>
    DEBUG && console.warn('[FoundationPlayer]', ...args),
  error: (...args: any[]) =>
    DEBUG && console.error('[FoundationPlayer]', ...args),
  debug: (...args: any[]) =>
    DEBUG && console.debug('[FoundationPlayer]', ...args),
  group: (label: string) =>
    DEBUG && console.group(`[FoundationPlayer] ${label}`),
  groupEnd: () => DEBUG && console.groupEnd(),
}

type HasVideoApi = {
  Video?: { getSXPKeysAsync?: () => Promise<string> }
}

function hasVideoApi(m: unknown): m is HasVideoApi {
  return !!(m as any)?.Video
}

function LoadingIndicatorIcon({
  className = 'h-6 w-6',
}: {
  className?: string
}) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
      role="img"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function LoadingOverlay({
  message = 'Das Video wird geladen ...',
}: {
  message?: string
}) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center bg-black/50 p-4">
      <LoadingIndicatorIcon className="h-8 w-8 text-white" />
      <h3 className="mt-2 text-base font-semibold leading-6 text-white">
        {message}
      </h3>
    </div>
  )
}

const FoundationPlayer = ({
  video,
  postRollDisabled,
  autoplay = false,
  currentVideoIndex = 0,
  isAdPlayingListener,
  currentAdTimeListener,
  contentFinishedListener,
  videoPlayingListener,
  showRelatedVideosListener,
  fast = false,
}: FoundationPlayerProps) => {
  const ivwCode = '/wetterde/videoabrufe/news/artikel'
  const noVideoAds = false

  const consent = useTcString()
  const tcString = consent?.tcString ?? ''
  const isConsentReady = !!tcString

  const adsDisabled = noVideoAds || video?.fields['ads-free'] === 'true'
  const playerRef = useRef<PlayerInterface | null>(null)
  const playerElementRef = useRef<HTMLDivElement>(null)

  const posterUrl = video?.image?.src
    ? new URL(video.image.src).pathname
    : FALLBACK_IMAGE_URL

  const [playerCreated, setPlayerCreated] = useState<boolean>(false)
  const [sxpKeys, setSxpKeys] = useState<string | undefined>()
  const [isVideoAllowed, setIsVideoAllowed] = useState<boolean>(true)
  const [videoErrorMessage, setVideoErrorMessage] =
    useState<string>(defaultErrorMessage)
  const [isGeoAllowed, setIsGeoAllowed] = useState<boolean>(true)
  const [isDateAllowed, setIsDateAllowed] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState(true)
  const [hadError, setHadError] = useState(false)

  // Spinner timeout failsafe
  useEffect(() => {
    if (!isLoading) return
    const id = window.setTimeout(() => setIsLoading(false), 12000)
    return () => window.clearTimeout(id)
  }, [isLoading])

  // GEO/Date gates
  useEffect(() => {
    if (!video) {
      log.info('waiting for video prop before gates')
      return
    }
    log.group('Gates: geo/date check')

    apiFetch('/api/geoip/')
      .then((data) => {
        const regionsAllowed: GeoCountries = toGeoCountries(
          video?.fields?.['regions-allowed'],
        )
        const raw = data?.country
        const country = String(raw ?? '')
          .trim()
          .toLowerCase()
        const allowed = geoAllowed(regionsAllowed, country)
        log.debug('GeoIP result:', {
          regionsAllowed,
          apiCountryRaw: raw,
          apiCountry: country,
          allowed,
        })
        setIsGeoAllowed(allowed)
      })
      .catch((error) => {
        log.error('GeoIP Api fetch error:', error)
        setIsGeoAllowed(true) // fallback policy
      })

    const dateOk = !!video?.fields?.encoding
    log.debug(
      'Date allowed:',
      dateOk,
      'video.encoding:',
      video?.fields?.encoding,
    )
    setIsDateAllowed(dateOk)

    log.groupEnd()
  }, [video])

  useEffect(() => {
    if (!isGeoAllowed) {
      log.warn('Gate: blocked by geo')
      setIsVideoAllowed(false)
      setVideoErrorMessage(geoBlockingErrorMessage)
      setIsLoading(false)
    } else if (!isDateAllowed) {
      log.warn('Gate: blocked by date')
      setIsVideoAllowed(false)
      setVideoErrorMessage(dateErrorMessage)
      setIsLoading(false)
    } else {
      setIsVideoAllowed(true)
      setVideoErrorMessage(defaultErrorMessage)
    }
  }, [isGeoAllowed, isDateAllowed])

  useEffect(() => {
    log.info('Consent update', {
      isConsentReady,
      hasTcString: !!tcString,
      tcString: DEBUG ? tcString : '(hidden)',
    })
  }, [isConsentReady, tcString])

  // SXP keys
  useEffect(() => {
    log.group('SXP keys fetch')
    let timeoutId: number | undefined

    const molten =
      typeof window !== 'undefined' ? (window as any).MoltenBundle : undefined

    if (hasVideoApi(molten) && molten.Video?.getSXPKeysAsync) {
      log.debug('Fetching SXP keys immediately')
      molten.Video.getSXPKeysAsync().then((result: string) => {
        log.info('SXP keys ready')
        setSxpKeys(result)
      })
    } else {
      log.debug('getSXPKeysAsync missing; retry in 3s')
      timeoutId = window.setTimeout(() => {
        const laterMolten = (window as any).MoltenBundle
        if (hasVideoApi(laterMolten)) {
          laterMolten.Video?.getSXPKeysAsync?.().then((result: string) => {
            log.info('SXP keys ready (delayed)')
            setSxpKeys(result)
          })
        }
      }, 3000)
    }

    log.groupEnd()
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  // Create / recreate player (v2)
  async function initOrRecreatePlayer() {
    setIsLoading(true)
    setHadError(false)
    log.group('initOrRecreatePlayer()')

    try {
      if (!video) {
        log.warn('Abort: video prop is missing')
        return
      }
      if (!playerElementRef.current) {
        log.warn('Abort: playerElementRef is null')
        return
      }
      if (!sxpKeys) {
        log.warn('Abort: SXP keys not ready')
        return
      }
      if (!isVideoAllowed) {
        log.warn('Abort: gate blocked')
        return
      }

      const loadRequest: LoadRequest = createLoadRequest({
        video,
        tcString,
        ivwCode,
        moltenVideoTracking: sxpKeys,
        currentVideoIndex,
        postRoll: !postRollDisabled,
        poster: posterUrl,
        autoplay,
        fast,
        ads: !adsDisabled,
      })

      // tear down old instance
      if (playerRef.current) {
        try {
          await playerRef.current.destroy('recreate')
        } catch (error) {
          log.error('Error destroying player instance', error)
        }
        playerRef.current = null
      }

      const options: CreatePlayerOptions = {
        mountPoint: playerElementRef.current,
        loadRequest,
        ui: { enabled: true },
        initialLoadingSpinnerOptions: { enabled: false },
      }

      const player = await createPlayer(options)
      playerRef.current = player
      setPlayerCreated(true)
      setIsLoading(false)
      log.debug('Load request:', loadRequest)
      log.debug('Player created:', playerRef.current)

      // ---- STATE SUBSCRIPTIONS (aligned to PlayerState) ----

      // content started
      player.state.once(
        (s) => s.contentStarted,
        () => {
          log.info('State: contentStarted')
          setIsLoading(false)
          // to mark "playing" via state change:
          // videoPlayingListener?.(true);
        },
      )

      player.state.once(
        (s) => s.contentCompleted || s.state === 'ended',
        () => {
          log.info('State: contentCompleted/ended')
          contentFinishedListener?.(true)
          videoPlayingListener?.(false)
          showRelatedVideosListener?.(false)
        },
      )

      // timeline updates (ad or content)
      player.state.onValueChange(
        'currentTimeInSeconds',
        ({ currentTimeInSeconds }) => {
          if (typeof currentTimeInSeconds === 'number') {
            currentAdTimeListener?.(currentTimeInSeconds)
          }
        },
      )

      player.state.onValueChange('linearAdActive', ({ linearAdActive }) => {
        isAdPlayingListener?.(!!linearAdActive)
      })

      // Optional: listen to playback state (maps to `state` union)
      player.state.onValueChange('state', ({ state }) => {
        videoPlayingListener?.(state === 'playing')
      })
    } catch (e) {
      console.error('Error creating Player', (e as any).context)
      setHadError(true)
    } finally {
      setIsLoading(false)
      log.groupEnd()
    }
  }

  // Initial create
  useEffect(() => {
    const shouldCreate =
      isConsentReady &&
      sxpKeys &&
      isVideoAllowed &&
      !!video &&
      !!playerElementRef.current &&
      !playerRef.current // important: only if not already created

    if (shouldCreate) {
      log.info('✅ Conditions met → creating player')
      void initOrRecreatePlayer()
    } else {
      log.info('⏸️ Conditions not met; skipping create', {
        reason: {
          isConsentReady: !isConsentReady ? 'waiting for consent' : null,
          sxpKeys: !sxpKeys ? 'waiting for SXP keys' : null,
          isVideoAllowed: !isVideoAllowed ? 'blocked by gate' : null,
          video: !video ? 'waiting for video' : null,
          mount: !playerElementRef.current ? 'missing mount' : null,
          alreadyCreated: !!playerRef.current || null,
        },
      })
    }
  }, [isConsentReady, sxpKeys, isVideoAllowed, video])

  // 2a) Recreate on asset change
  useEffect(() => {
    if (!playerRef.current) return
    log.info('♻️ Recreate due to video change')
    void initOrRecreatePlayer()
  }, [video?.id])

  // 2b) Recreate on consent change
  useEffect(() => {
    if (!playerRef.current) return
    log.info('♻️ Recreate due to consent change')
    void initOrRecreatePlayer()
  }, [tcString])

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        log.info('Destroying player on unmount')
        playerRef.current.destroy('unmount').catch(() => {})
        playerRef.current = null
      }
    }
  }, [])

  return (
    <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
      {!playerCreated && (
        <img
          src={posterUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden
        />
      )}

      <div ref={playerElementRef} className="absolute inset-0" />

      {isVideoAllowed && !hadError && isLoading && <LoadingOverlay />}

      {!isVideoAllowed && (
        <div className="absolute inset-0 z-10 flex items-center justify-center text-center p-4">
          {videoErrorMessage}
        </div>
      )}
    </div>
  )
}

export default FoundationPlayer
